/**
 * Two-card user-scoped stat strip shown above the Annotation and
 * Adjudication queue tables.
 *
 *   Records in your queue (per-user)   |  Completed today
 *
 * Only "Records in your queue" has a real data source today (the queue
 * envelope's `total` from `useMyQueue` / `useReviewQueue`);
 * "Completed today" renders `—` until a metrics endpoint lands.
 * Renders the cards regardless so layout stays stable when the
 * placeholder is swapped for a real number.
 *
 * Reusable across queues — `recordsLabel` lets the host page swap the
 * card title for context (default: "Records in your queue"; the
 * adjudication page passes "Records awaiting adjudication").
 */
import React from 'react';

import { Card } from '../../../../../ui/card';

interface Props {
  /** From the queue envelope's `total` (e.g. `useMyQueue().data.total`). */
  recordsInQueue: number | null;
  /** No endpoint yet — pass `null` until one ships. */
  completedToday: number | null;
  /** Override the first card's label for non-personal queues. */
  recordsLabel?: string;
}

const fmt = (value: number | null): string =>
  value == null ? '—' : value.toLocaleString();

const StatCard: React.FC<{
  label: string;
  value: number | null;
  testId: string;
}> = ({ label, value, testId }) => (
  <Card
    className="flex flex-col items-start gap-1 p-4"
    data-testid={testId}
  >
    <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
      {label}
    </div>
    <div className="text-3xl font-bold tabular-nums text-foreground">
      {fmt(value)}
    </div>
  </Card>
);

const QueueStatStrip: React.FC<Props> = ({
  recordsInQueue,
  completedToday,
  recordsLabel = 'Records in your queue',
}) => (
  <section aria-label="Queue stats" data-testid="queue-stat-strip">
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
      <StatCard
        label={recordsLabel}
        value={recordsInQueue}
        testId="stat-records-in-queue"
      />
      <StatCard
        label="Completed today"
        value={completedToday}
        testId="stat-completed-today"
      />
    </div>
  </section>
);

export default QueueStatStrip;
